import { createContext } from 'react';
import type { AuthUser, LoginOutcome } from '@apis/auth';
import type { IConnectProviderOptions, ProviderName } from '@interfaces/IAuth';
import type { UnifiedProject } from '@interfaces/IUnifiedProject';

export type AuthContextValue = {
  user: AuthUser | null;
  isHydrating: boolean;
  /**
   * Password step of the sign-in flow. Resolves to the outcome rather than a
   * user, because an MFA-protected account answers with a challenge or a
   * forced-enrollment marker instead of a session; `user` is only set on the
   * `authenticated` outcome.
   */
  login: (email: string, password: string) => Promise<LoginOutcome>;
  /**
   * Adopt the session the MFA leg just promoted (`mfa/verify` or
   * `mfa/enroll/confirm` returning `{user}`). Same post-login bookkeeping as a
   * password-only sign-in, so the app can't tell the two paths apart.
   */
  completeMfaLogin: (user: AuthUser, projects?: UnifiedProject[]) => Promise<void>;
  /** Local patch after the security settings toggle MFA on/off. */
  setMfaEnabled: (enabled: boolean) => void;
  logout: () => void;
  connectProvider: (
    provider: ProviderName,
    username: string,
    password: string,
    opts?: IConnectProviderOptions,
  ) => Promise<void>;
  disconnectProvider: (provider: ProviderName) => Promise<void>;
  refreshUser: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);
